/**
 * Live preview of a pending change (W2-05; DESIGN.md §5.7 "hovering a cell temporarily applies it to the preview",
 * §4.13 "Preview loops the span in the preview").
 *
 * The change runs as a live Gesture on the DocStore: preview, timeline and inspector follow it, nothing reaches the
 * history until `commit` (templates' Apply), and `end` cancels it (picker hover, Esc, close). While a change is
 * shown and playback is stopped, the preview loops over the span (`PreviewUiState.timeOverride`: the playhead, the
 * timeline and the audio stay where they are) so the real motion animates; when the preview ends the preview follows
 * the transport again. Reduced motion: the preview shows the middle of the span instead of looping. Starting
 * playback ends the loop (the preview follows the playing transport).
 */

import type { AppServices } from '../../app/registry/types';
import type { TxBuilder, Txn } from '../../core/ops/types';
import type { Sec } from '../../core/time/types';
import type { MessageRef } from '../../core/util/types';
import type { Gesture, TxnMeta, TxnOutcome } from '../../state/types';

export interface PreviewSpan {
  readonly t0: Sec;
  readonly t1: Sec;
}

export interface LivePreview {
  /**
   * Shows `build` applied to the document (null: the document as it is) and loops `span`. Returns the outcome of the
   * gesture update (null when nothing is shown or no project is open).
   */
  show<R>(build: ((tx: TxBuilder) => R) | null, span: PreviewSpan | null): TxnOutcome<R> | null;
  /** Keeps the change and records it as one undoable step (null when nothing was shown or the store refused it). */
  commit(): Txn | null;
  /** Drops the change; the preview follows the transport again. */
  end(): void;
  /** Progress (0..1) of the preview time inside the looped span, or null when not looping. */
  progress(): number | null;
  readonly active: boolean;
}

/** Pause at the end of each loop before it starts over. */
const LOOP_PAUSE_SEC = 0.45;
/** The loop never plays faster than this (very short spans). */
const MIN_LOOP_SEC = 0.25;

let seq = 0;

export interface LivePreviewOptions {
  readonly meta: TxnMeta;
  /** Animate by looping the preview time (false: the middle of the span, still). */
  readonly loop: boolean;
  /** Error reporting for failed updates (the previous state stays shown). */
  readonly onFail?: (reason: MessageRef) => void;
  /** Called when the preview starts or stops looping (`progress()` turns from null to numbers or back). */
  readonly onLoopChange?: () => void;
}

/**
 * Progress of a looping preview for the value plot's moving dot: polled every frame while it returns numbers, and
 * woken through `subscribe` when looping starts again (so nothing polls while nothing loops).
 */
export interface ProgressSource {
  progress(): number | null;
  subscribe(listener: () => void): () => void;
}

/** A stable ProgressSource over the LivePreview a dialog has now (dialogs recreate theirs when options change). */
export interface ProgressRelay extends ProgressSource {
  /** The preview whose progress is read (null: none). */
  attach(preview: Pick<LivePreview, 'progress'> | null): void;
  /** Wakes the subscribers; pass it as `LivePreviewOptions.onLoopChange`. */
  readonly notify: () => void;
}

export function createProgressRelay(): ProgressRelay {
  const listeners = new Set<() => void>();
  let current: Pick<LivePreview, 'progress'> | null = null;
  const notify = (): void => {
    for (const l of [...listeners]) l();
  };
  return {
    progress: () => current?.progress() ?? null,
    subscribe(listener) {
      listeners.add(listener);
      return () => {
        listeners.delete(listener);
      };
    },
    attach(preview) {
      current = preview;
      notify();
    },
    notify,
  };
}

export function createLivePreview(services: AppServices, opts: LivePreviewOptions): LivePreview {
  const frameId = `easing.livePreview.${String(++seq)}`;
  let gesture: Gesture | null = null;
  let gestureStore: AppServices['store'] = null;
  let span: PreviewSpan | null = null;
  /** The time this preview put on the preview (cleared only while it is still ours). */
  let shown: Sec | null = null;
  let unsub: (() => void) | null = null;
  let startedAt = 0;

  const playing = (): boolean => services.audio.transport.state.playing;
  const showTime = (t: Sec | null): void => {
    const editor = services.editor.getState();
    if (t === null && editor.preview.timeOverride !== shown) {
      shown = null;
      return;
    }
    shown = t;
    editor.setPreview({ timeOverride: t });
  };

  const stopLoop = (): void => {
    if (unsub === null) return;
    unsub();
    unsub = null;
    opts.onLoopChange?.();
  };

  const restore = (): void => {
    if (shown !== null) showTime(null);
  };

  const frame = (now: number): void => {
    if (span === null || unsub === null) return;
    if (playing()) {
      // The user started playback: the preview follows the playing transport.
      stopLoop();
      restore();
      return;
    }
    const len = Math.max(span.t1 - span.t0, 0);
    const period = Math.max(len, MIN_LOOP_SEC) + LOOP_PAUSE_SEC;
    const elapsed = ((now - startedAt) / 1000) % period;
    const local = Math.min(elapsed, Math.max(len, MIN_LOOP_SEC)) / Math.max(len, MIN_LOOP_SEC);
    showTime(span.t0 + len * Math.min(local, 1));
    services.scheduler.request(frameId);
  };

  const startLoop = (s: PreviewSpan): void => {
    span = s;
    if (playing()) return;
    if (!opts.loop) {
      stopLoop();
      showTime((s.t0 + s.t1) / 2);
      return;
    }
    if (unsub === null) {
      startedAt = typeof performance === 'undefined' ? Date.now() : performance.now();
      showTime(s.t0);
      unsub = services.scheduler.add(frameId, 'transport', frame);
      opts.onLoopChange?.();
    }
    services.scheduler.request(frameId);
  };

  const cancelGesture = (): void => {
    if (gesture?.active === true) gesture.cancel();
    gesture = null;
    gestureStore = null;
  };

  return {
    show<R>(build: ((tx: TxBuilder) => R) | null, s: PreviewSpan | null): TxnOutcome<R> | null {
      const store = services.store;
      if (store === null) return null;
      if (gestureStore !== null && gestureStore !== store) cancelGesture();
      let outcome: TxnOutcome<R> | null = null;
      if (build === null) {
        if (gesture?.active === true) gesture.update(() => undefined);
      } else {
        if (gesture?.active !== true) {
          gesture = store.beginGesture(opts.meta);
          gestureStore = store;
        }
        outcome = gesture.update(build);
        if (!outcome.ok) {
          opts.onFail?.(outcome.reason);
          // Show the document as it is rather than a stale change.
          gesture.update(() => undefined);
        }
      }
      if (s !== null && build !== null) startLoop(s);
      else {
        stopLoop();
        restore();
      }
      return outcome;
    },
    commit() {
      stopLoop();
      restore();
      const g = gesture;
      gesture = null;
      gestureStore = null;
      if (g?.active !== true) return null;
      return g.commit();
    },
    end() {
      stopLoop();
      cancelGesture();
      restore();
      span = null;
    },
    progress() {
      if (span === null || unsub === null || shown === null) return null;
      const len = span.t1 - span.t0;
      if (!(len > 0)) return null;
      return Math.min(Math.max((shown - span.t0) / len, 0), 1);
    },
    get active() {
      return gesture?.active === true;
    },
  };
}
